import Badge from "@/components/shared/Badge/Badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type {
  EnotasNfseEnsureResponse,
  NotazzNfseEnsureResponse,
} from "@/services/sells.service";
import { SellsService } from "@/services/sells.service";
import { getIntegrationWithIcon } from "@/utils/integrations";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  ExternalLink,
  FileText,
  Loader2,
} from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatInvoiceEmitError,
  formatInvoiceIssues,
} from "@/utils/formatInvoiceEmitError";
import { mapInvoiceStatusToBadge } from "@/utils/invoiceStatus";

type Provider = "notazz" | "enotas";
type ModalPhase = "checking" | "choose" | "processing" | "success" | "error";

type SuccessData = {
  statusNota: string | null;
  pdf: string | null;
  xml: string | null;
  numero: string | null;
  codigoVerificacao: string | null;
  linkPrefeitura?: string | null;
};

type AvailabilityValue = {
  available: boolean;
  message?: string;
  reason?: string;
  issues?: Array<{
    field?: string;
    message?: string;
    missing_fields?: string[];
  }>;
};

interface EmitNoteFiscalButtonProps {
  orderUuid: string;
  orderStatus: string;
  onSuccess?: () => void;
}

export default function EmitNoteFiscalButton({
  orderUuid,
  orderStatus,
  onSuccess,
}: EmitNoteFiscalButtonProps) {
  const { t } = useTranslation(["sales", "common"]);
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<ModalPhase>("checking");
  const [errorDetails, setErrorDetails] = useState("");
  const [successData, setSuccessData] = useState<SuccessData | null>(null);
  const [lastProvider, setLastProvider] = useState<Provider | null>(null);
  const isOpenRef = useRef(false);

  const emitErrorMessages = {
    timeout: t("sales:noteFiscalTimeout"),
    fallback: t("sales:errorEmittingNoteFiscal"),
  };

  const applySuccess = (
    data: NotazzNfseEnsureResponse | EnotasNfseEnsureResponse,
  ) => {
    if (data.success) {
      onSuccess?.();
      toast({
        title:
          data.action === "emitted"
            ? t("sales:noteFiscalEmitted")
            : t("sales:noteFiscalStatusConsulted"),
        description: data.message,
        variant: "success",
      });
      if (!isOpenRef.current) return;
      setSuccessData(data.data ?? null);
      setPhase("success");
      return;
    }
    if (!isOpenRef.current) return;
    setPhase("error");
    setErrorDetails(data.message);
  };

  const applyError = (error: unknown) => {
    const details = formatInvoiceEmitError(error, emitErrorMessages);
    if (!isOpenRef.current) {
      toast({
        title: t("sales:errorEmittingNoteFiscal"),
        description: details,
        variant: "destructive",
      });
      return;
    }
    setPhase("error");
    setErrorDetails(details);
  };

  const notazzMutation = useMutation({
    mutationFn: () => SellsService.getNotazzNfseEnsureAndStatus(orderUuid),
    onSuccess: applySuccess,
    onError: applyError,
  });

  const enotasMutation = useMutation({
    mutationFn: () => SellsService.getEnotasNfseEnsureAndStatus(orderUuid),
    onSuccess: applySuccess,
    onError: applyError,
  });

  const formatProviderUnavailable = (
    result: PromiseSettledResult<AvailabilityValue>,
    providerLabel: string,
  ): { line: string; isNotConfigured: boolean } | null => {
    if (result.status === "rejected") {
      return {
        line: `${providerLabel}: ${t("sales:errorCheckingAvailability")}`,
        isNotConfigured: false,
      };
    }
    const value = result.value;
    if (value.available) return null;
    const isNotConfigured =
      value.reason === "INTEGRATION_NOT_FOUND" ||
      value.reason === "SERVICE_NOT_AVAILABLE";
    const line =
      value.issues && value.issues.length > 0
        ? formatInvoiceIssues(value.issues)
        : (value.message ?? t("sales:noteFiscalNotAvailable"));
    return { line, isNotConfigured };
  };

  const startProcess = (provider: Provider) => {
    setLastProvider(provider);
    setPhase("processing");
    setErrorDetails("");
    setSuccessData(null);
    if (provider === "notazz") {
      notazzMutation.mutate();
    } else {
      enotasMutation.mutate();
    }
  };

  const checkAvailability = async () => {
    setPhase("checking");
    setErrorDetails("");
    setSuccessData(null);

    const [notazzResult, enotasResult] = await Promise.allSettled([
      SellsService.checkNotazzAvailability(orderUuid),
      SellsService.checkEnotasAvailability(orderUuid),
    ]);

    if (!isOpenRef.current) return;

    const notazzAvailable =
      notazzResult.status === "fulfilled" && notazzResult.value.available;
    const enotasAvailable =
      enotasResult.status === "fulfilled" && enotasResult.value.available;

    if (notazzAvailable && enotasAvailable) {
      setPhase("choose");
      return;
    }
    if (notazzAvailable) {
      startProcess("notazz");
      return;
    }
    if (enotasAvailable) {
      startProcess("enotas");
      return;
    }

    const notazzInfo = formatProviderUnavailable(notazzResult, "Notazz");
    const enotasInfo = formatProviderUnavailable(enotasResult, "eNotas");
    const notazzHasRequirementsIssue = Boolean(
      notazzInfo && !notazzInfo.isNotConfigured,
    );
    const enotasHasRequirementsIssue = Boolean(
      enotasInfo && !enotasInfo.isNotConfigured,
    );
    const parts: string[] = [];
    if (notazzInfo && !notazzInfo.isNotConfigured) {
      parts.push(
        t("sales:notazzOrderRequirementsNotMet") + "\n" + notazzInfo.line,
      );
    } else if (notazzInfo && !enotasHasRequirementsIssue) {
      parts.push("Notazz: " + notazzInfo.line);
    }
    if (!notazzHasRequirementsIssue) {
      if (enotasInfo && enotasInfo.isNotConfigured) {
        parts.push(t("sales:enotasNotConfiguredShort"));
      } else if (enotasInfo) {
        parts.push("eNotas: " + enotasInfo.line);
      }
    }

    setPhase("error");
    setErrorDetails(
      parts.length > 0 ? parts.join("\n\n") : t("sales:noteFiscalNotAvailable"),
    );
    setLastProvider(null);
  };

  const resetState = () => {
    setPhase("checking");
    setErrorDetails("");
    setSuccessData(null);
    setLastProvider(null);
    notazzMutation.reset();
    enotasMutation.reset();
  };

  const handleOpen = () => {
    isOpenRef.current = true;
    setOpen(true);
    void checkAvailability();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    isOpenRef.current = nextOpen;
    setOpen(nextOpen);
    if (!nextOpen && phase !== "processing") {
      resetState();
    }
  };

  const handleRetry = () => {
    if (lastProvider) {
      startProcess(lastProvider);
      return;
    }
    void checkAvailability();
  };

  const titleByPhase: Record<ModalPhase, string> = {
    checking: t("sales:emitNoteFiscal"),
    choose: t("sales:chooseNoteFiscalProvider"),
    processing: t("sales:processingNoteFiscal"),
    success:
      successData?.statusNota === "Autorizada"
        ? t("sales:noteFiscalAuthorized")
        : t("sales:noteFiscalEmitted"),
    error: t("sales:errorEmittingNoteFiscal"),
  };

  const showFooter = phase !== "checking";
  const isDisabled = orderStatus !== "paid";

  const button = (
    <Button
      variant="secondary"
      className="w-full lg:w-auto"
      onClick={handleOpen}
      disabled={isDisabled}
    >
      <FileText size={16} />
      {t("sales:emitNoteFiscal")}
    </Button>
  );

  if (orderStatus !== "paid") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent className="max-w-xs whitespace-pre-wrap">
            <p>{t("sales:noteFiscalOnlyForPaidOrders")}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <>
      {button}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden p-0">
          <div className="flex flex-col max-h-[90vh]">
            <DialogHeader className="p-3 sm:p-6 pb-2 sm:pb-4 border-b border-colorBorder shrink-0">
              <div className="flex items-center justify-between pr-6 sm:pr-8 gap-2">
                <DialogTitle className="text-base sm:text-xl font-semibold truncate">
                  {titleByPhase[phase]}
                </DialogTitle>
                {phase === "success" && successData?.statusNota && (
                  <Badge
                    status={mapInvoiceStatusToBadge(successData.statusNota)}
                    text={successData.statusNota}
                  />
                )}
              </div>
            </DialogHeader>

            <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-4">
              <AnimatePresence mode="wait">
                {phase === "checking" ? (
                  <motion.div
                    key="checking"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="bg-zinc-900/50 rounded-lg p-4 border border-colorBorder flex items-center gap-3"
                  >
                    <Loader2 className="animate-spin text-muted-foreground shrink-0" size={18} />
                    <p className="text-sm text-muted-foreground">
                      {t("sales:checkingNoteFiscalAvailability")}
                    </p>
                  </motion.div>
                ) : phase === "choose" ? (
                  <motion.div
                    key="choose"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    <p className="text-sm text-muted-foreground">
                      {t("sales:chooseNoteFiscalProviderDescription")}
                    </p>
                    <button
                      type="button"
                      onClick={() => startProcess("notazz")}
                      className={cn(
                        "w-full text-left bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder",
                        "hover:border-primary/40 transition-colors",
                      )}
                    >
                      {getIntegrationWithIcon("notazz") || (
                        <span className="text-colorText text-sm font-medium">
                          {t("sales:processWithNotazz")}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => startProcess("enotas")}
                      className={cn(
                        "w-full text-left bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder",
                        "hover:border-primary/40 transition-colors",
                      )}
                    >
                      {getIntegrationWithIcon("enotas") || (
                        <span className="text-colorText text-sm font-medium">
                          {t("sales:processWithEnotas")}
                        </span>
                      )}
                    </button>
                  </motion.div>
                ) : phase === "processing" ? (
                  <motion.div
                    key="processing"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="bg-zinc-900/50 rounded-lg p-4 border border-colorBorder"
                  >
                    <p className="text-sm text-colorText">
                      {t("sales:pleaseWait")}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("sales:emittingNoteFiscal")}
                    </p>
                  </motion.div>
                ) : phase === "success" ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-4"
                  >
                    <p className="text-sm text-muted-foreground">
                      {successData?.statusNota === "Autorizada"
                        ? t("sales:noteFiscalAuthorizedDescription")
                        : t("sales:noteFiscalEmittedSuccessfully")}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      {successData?.numero && (
                        <div className="bg-zinc-900/50 rounded-lg p-3 border border-colorBorder">
                          <p className="text-muted-foreground text-[10px] sm:text-xs mb-1">
                            {t("sales:invoiceNumber")}
                          </p>
                          <p className="text-colorText text-sm font-medium">
                            {successData.numero}
                          </p>
                        </div>
                      )}
                      {successData?.codigoVerificacao && (
                        <div className="bg-zinc-900/50 rounded-lg p-3 border border-colorBorder">
                          <p className="text-muted-foreground text-[10px] sm:text-xs mb-1">
                            {t("sales:invoiceVerificationCode")}
                          </p>
                          <p className="text-colorText text-sm font-medium">
                            {successData.codigoVerificacao}
                          </p>
                        </div>
                      )}
                    </div>
                    {(successData?.pdf ||
                      successData?.xml ||
                      successData?.linkPrefeitura) && (
                      <div className="flex flex-col gap-2">
                        {successData.pdf && (
                          <Button variant="secondary" asChild>
                            <a
                              href={successData.pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <FileText size={16} />
                              {t("sales:viewNotePdf")}
                              <ExternalLink size={14} />
                            </a>
                          </Button>
                        )}
                        {successData.xml && (
                          <Button variant="secondary" asChild>
                            <a
                              href={successData.xml}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <FileText size={16} />
                              {t("sales:viewNoteXml")}
                              <ExternalLink size={14} />
                            </a>
                          </Button>
                        )}
                        {successData.linkPrefeitura && (
                          <Button variant="secondary" asChild>
                            <a
                              href={successData.linkPrefeitura}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink size={16} />
                              {t("sales:viewCityHall")}
                            </a>
                          </Button>
                        )}
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="rounded-lg border border-red-500/30 bg-red-500/5 p-4"
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle
                        className="text-red-400 shrink-0 mt-0.5"
                        size={16}
                      />
                      <div>
                        <p className="text-sm font-medium text-colorText mb-1">
                          {t("sales:errorDetails")}
                        </p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {errorDetails}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {showFooter && (
              <div className="p-3 sm:p-6 pt-3 sm:pt-4 border-t border-colorBorder bg-background shrink-0 space-y-2">
                {phase === "processing" && (
                  <Button disabled className="w-full h-9 sm:h-10 text-xs sm:text-sm">
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    {t("common:processing")}
                  </Button>
                )}
                {phase === "choose" && (
                  <Button
                    variant="outline"
                    className="w-full h-9 sm:h-10 text-xs sm:text-sm"
                    onClick={() => handleOpenChange(false)}
                  >
                    {t("common:close")}
                  </Button>
                )}
                {phase === "success" && (
                  <Button
                    variant="outline"
                    className="w-full h-9 sm:h-10 text-xs sm:text-sm"
                    onClick={() => handleOpenChange(false)}
                  >
                    {t("common:close")}
                  </Button>
                )}
                {phase === "error" && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                      onClick={handleRetry}
                    >
                      {t("sales:noteFiscalTryAgain")}
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                      onClick={() => handleOpenChange(false)}
                    >
                      {t("common:close")}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
